import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import * as phases from 'constants/phases';

import { fetch as fetchCards } from 'actions/cards';
import { fetchAvailable as fetchAvailableStorylets } from 'actions/storylet';
import { fetch as fetchMap } from 'actions/map';

import GeneralContainer from 'components/GeneralContainer';
import Map from 'components/Map';
import Loading from 'components/Loading';
import StoryletsAvailable from 'components/StoryletsAvailable';
import StoryletIn from 'components/StoryletIn';
import StoryletEnd from 'components/StoryletEnd';
import Act from 'components/Act';
import ExternalAct from 'components/ExternalAct';
import Rename from 'components/Rename';
import SecondChance from 'components/SecondChance';
import UniqueActPending from 'components/UniqueActPending';
import { IAppState } from "types/app";
import DomManipulationContext from 'components/DomManipulationContext';
import ExceptionalFriendModal from 'components/ExceptionalFriendModal';

export interface Props extends ReturnType<typeof mapStateToProps> {
  dispatch: Function,
}

export interface State {
  isExceptionalFriendModalOpen: boolean,
}

class StoryTabContent extends React.Component<Props, State> {
  static displayName = 'StoryTabContent';

  state = {
    isExceptionalFriendModalOpen: false,
  };

  componentDidMount = () => {
    const {
      dispatch,
      handSize,
      isFetching,
      phase,
      showOps,
      socialAct,
      storylet,
      storylets,
    } = this.props;

    if (isFetching) {
      return;
    }

    const storyletsAreFalsy = !((storylets && storylets.length) || storylet);
    const weNeedToBackOutOfASocialAct = phase === 'Act' && !socialAct;

    // If we have falsy values for both 'storylets' and 'storylet', or we have stale social act state,
    if (storyletsAreFalsy || weNeedToBackOutOfASocialAct) {
      dispatch(fetchAvailableStorylets());
    }

    // If we're in a place where we should be displying opportunity
    // cards, then check that we have them in the store
    if (showOps) {
      // If we don't have the expected state structure for opp cards,
      // go fetch them. handSize should never be 0
      if (!handSize) {
        dispatch(fetchCards());
      }
    }
  };

  renderContent() {
    const { isFetching, phase } = this.props;
    if (isFetching) {
      return <Loading />;
    }
    switch (phase) {
      case phases.ACT:
        return <Act />;
      case phases.END:
        return <StoryletEnd />;
      case phases.EXTERNAL_ACT:
        return <ExternalAct />;
      case phases.IN: // fall-through; these are the same for slet rendering
      case phases.IN_ITEM_USE:
        return <StoryletIn />;
      case phases.RENAME:
        return <Rename />;
      case phases.SECOND_CHANCE:
        return <SecondChance />;
      case phases.AVAILABLE:
        return <StoryletsAvailable />;
      case phases.UNIQUE_ACT_PENDING:
        return <UniqueActPending />;
      default: // null. TODO: decide to do something about this.
        return null;
    }
  }

  handleOpenSubscriptionModal = () => {
    this.setState({ isExceptionalFriendModalOpen: true });
  };

  handleRequestCloseSubscriptionModal = (didUserSubscribe: boolean) => {
    this.setState({ isExceptionalFriendModalOpen: false });
    const { dispatch } = this.props;
    // If the user subscribed, we need to update storylet and map state
    if (didUserSubscribe) {
      dispatch(fetchAvailableStorylets());
      dispatch(fetchMap());
    }
  };

  /**
   * Render
   * @return {Object}
   */
  render() {
    const { isExceptionalFriendModalOpen } = this.state;
    return (
      <Fragment>
        <DomManipulationContext.Provider
          value={{
            onOpenSubscriptionModal: this.handleOpenSubscriptionModal,
          }}
        >
          <GeneralContainer>
            {this.renderContent()}
          </GeneralContainer>
        </DomManipulationContext.Provider>
        <ExceptionalFriendModal
          isOpen={isExceptionalFriendModalOpen}
          onRequestClose={this.handleRequestCloseSubscriptionModal}
        />
        <Map />
      </Fragment>
    );
  }
}

const mapStateToProps = ({
  cards: { handSize },
  map: { showOps },
  storylet: {
    isFetching,
    phase,
    socialAct,
    storylet,
    storylets,
  },
}: IAppState) => ({
  handSize,
  showOps,
  isFetching,
  phase,
  socialAct,
  storylet,
  storylets,
});

export default withRouter(connect(mapStateToProps)(StoryTabContent));
